<template>
  <div>
    <!-- Filter bar -->
    <b-card
      no-body
      style="margin-bottom: 2rem"
    >
      <b-card-body>
        <b-row align-v="center">
          <b-col
            class="d-flex align-items-center"
            style="gap: 8px;"
          >
            <b-form-select
              v-model="filterType"
              :options="$tOptions(typeOptions)"
              size="sm"
              class="filter-select"
            />
            <b-form-select
              v-model="filterMode"
              :options="$tOptions(modeOptions)"
              size="sm"
              class="filter-select"
            />
            <b-form-select
              v-if="filterMode === 'current'"
              v-model="filterStatus"
              :options="$tOptions(statusOptions)"
              size="sm"
              class="filter-select"
            />
            <b-button
              variant="outline-secondary"
              size="sm"
              class="text-nowrap"
              @click="resetFilters"
            >
              {{ $t('ล้างตัวกรอง') }}
            </b-button>
          </b-col>
          <b-col
            v-if="isMaster"
            cols="12"
            lg="auto"
            class="d-flex align-items-center agent-row mt-1 mt-lg-0"
            style="gap: 8px; margin-left: auto;"
          >
            <div class="agent-selector-width">
              <AgentSelector
                v-model="selectedAgent"
                size="sm"
                :placeholder="$t('เลือกตัวแทน...')"
                @change="onAgentChange"
              />
            </div>
            <b-form-checkbox
              v-model="includeDownlines"
              switch
              class="agent-filter-checkbox text-nowrap"
              :disabled="!selectedAgent || !selectedAgent.code || selectedAgent.code === 'master'"
              @change="fetchData"
            >
              {{ $t('รวมตัวแทนย่อย') }}
            </b-form-checkbox>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Loading state -->
    <div
      v-if="loading"
      class="text-center py-5"
    >
      <b-spinner variant="primary" />
    </div>

    <!-- Cards grid -->
    <b-row
      v-else
      class="match-height"
      style="row-gap: 2rem"
    >
      <b-col
        v-for="item in filteredItems"
        :key="item.id"
        xl="4"
        md="6"
        sm="12"
      >
        <result-card
          :item="item"
          :use-last-result="useLastResult"
          style="margin-bottom: 0"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
/* eslint-disable */
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BFormSelect,
  BFormCheckbox,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import lottoService from '@/service/lottoService'
import reportService from '@/service/reportService'
import AgentSelector from '@core/components/agent/AgentSelector.vue'
import ResultCard from '@/views/dashboard/result/ResultCard.vue'

const FILTER_STORAGE_KEY = 'result-dashboard-filters'
function loadSavedFilters() {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch { return null }
}
function saveFilters(filters) {
  localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters))
}

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormSelect,
    BFormCheckbox,
    BButton,
    BSpinner,
    AgentSelector,
    ResultCard,
  },
  data() {
    const saved = loadSavedFilters()
    return {
      items: [],
      loading: true,
      filterType: null,
      filterMode: 'latest',
      filterStatus: null,
      interval: null,
      selectedAgent: saved?.selectedAgent || null,
      includeDownlines: saved?.includeDownlines || false,
      typeOptions: [
        { value: null, text: 'ทุกประเภท' },
        { value: 1, text: 'รัฐบาล' },
        { value: 2, text: 'ธนาคาร' },
        { value: 'stock', text: 'หุ้น' },
        { value: 5, text: 'เลขชุด' },
      ],
      modeOptions: [
        { value: 'latest', text: 'ผลรอบล่าสุด' },
        { value: 'current', text: 'รอบปัจจุบัน' },
      ],
      statusOptions: [
        { value: null, text: 'ทุกสถานะ' },
        { value: 1, text: 'เปิดรับแทง' },
        { value: 3, text: 'ปิดรับแทง' },
        { value: 7, text: 'กำลังจ่าย' },
        { value: 9, text: 'จ่ายเสร็จ' },
      ],
    }
  },
  computed: {
    isMaster() {
      return this.$store.state.app.isMaster(this.$store)
    },
    useLastResult() {
      return this.filterMode === 'latest'
    },
    filteredItems() {
      return this.items
        .filter(item => {
          if (this.filterType !== null) {
            if (this.filterType === 'stock') {
              if (![3, 4].includes(item.type)) return false
            } else if (item.type !== this.filterType) return false
          }
          if (this.filterMode === 'current' && this.filterStatus !== null && item.status !== this.filterStatus) return false
          return true
        })
        .sort((a, b) => {
          if (this.useLastResult) {
            return this.parseDate(b.lastResult ? b.lastResult.roundDate : b.roundDate)
              - this.parseDate(a.lastResult ? a.lastResult.roundDate : a.roundDate)
          }
          const aHasResult = a.status >= 5 ? 1 : 0
          const bHasResult = b.status >= 5 ? 1 : 0
          if (bHasResult !== aHasResult) return bHasResult - aHasResult
          return this.parseDate(b.roundDate) - this.parseDate(a.roundDate)
        })
    },
  },
  watch: {
    selectedAgent() { this.persistFilters() },
    includeDownlines() { this.persistFilters() },
  },
  async mounted() {
    if (!this.isMaster) {
      await this.fetchData()
    }
    this.interval = setInterval(() => {
      this.pollStatus()
    }, 10000)
  },
  destroyed() {
    if (this.interval) {
      clearInterval(this.interval)
    }
  },
  methods: {
    persistFilters() {
      saveFilters({
        selectedAgent: this.selectedAgent,
        includeDownlines: this.includeDownlines,
      })
    },
    buildAgentParams() {
      const params = new URLSearchParams()
      if (this.isMaster && this.selectedAgent && this.selectedAgent.code) {
        params.set('agent', this.selectedAgent.code)
      }
      if (this.isMaster && this.includeDownlines) {
        params.set('includeDownlines', 'true')
      }
      return params.toString()
    },
    onAgentChange() {
      if (!this.selectedAgent || !this.selectedAgent.code || this.selectedAgent.code === 'master') {
        this.includeDownlines = false
      }
      this.fetchData()
    },
    async fetchData() {
      this.loading = true
      try {
        const agentParams = this.buildAgentParams()
        const url = agentParams ? `dashboard/summary?${agentParams}` : 'dashboard/summary'
        const res = await reportService.getAuthen(url)
        if (res.status === 200) {
          this.items = res.data
        }
      } finally {
        this.loading = false
      }
    },
    pollStatus() {
      if (document.hidden || this.items.length === 0) return
      lottoService
        .getAuthen('huay/states')
        .then(res => {
          if (res.status === 200) {
            for (let i = 0; i < this.items.length; i += 1) {
              const newStatus = res.data[this.items[i].id]
              if (newStatus !== undefined && this.items[i].status !== newStatus) {
                this.items[i].status = newStatus
              }
            }
          }
        })
        .catch(() => {})
    },
    parseDate(dateStr) {
      if (!dateStr) return 0
      // format: "dd/MM/yyyy HH:mm:ss"
      const [d, m, y] = dateStr.split(' ')[0].split('/')
      return new Date(`${y}-${m}-${d}`).getTime() || 0
    },
    resetFilters() {
      this.filterType = null
      this.filterMode = 'latest'
      this.filterStatus = null
    },
  },
}
</script>

<style scoped>
.filter-select {
  width: 100%;
  max-width: 200px;
}
.agent-selector-width {
  min-width: 250px;
  max-width: 350px;
  flex: 1;
}
.agent-selector-width >>> .vs__selected {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.agent-selector-width >>> .vs__dropdown-menu {
  min-width: 280px;
}
.agent-selector-width >>> .vs__dropdown-option {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
@media (max-width: 450px) {
  .agent-row {
    flex-wrap: wrap;
  }
  .agent-selector-width {
    width: 100%;
  }
  .agent-row .agent-filter-checkbox {
    margin-left: auto;
  }
}
.agent-filter-checkbox {
  margin-bottom: 0;
}
.agent-filter-checkbox >>> .custom-control-label {
  margin-bottom: 0;
}
</style>

